import {
  applyAttitudeToSpirit,
  calculateResetSpirit,
  calculateSpiritAfterUpdate,
  getPerformanceCoefficient,
  inferAttitudeFromSpirits,
  parseNumber,
  roundSpirit,
} from '../teamSpiritUtils'

const getVenueFactor = (venue, homeAdvantageFactor) =>
  venue === 'Home' ? homeAdvantageFactor : 1

const getCountryInputFromRow = (
  row,
  country,
  activeNationalTeamName,
  defaultCountryValues
) => {
  if (country === activeNationalTeamName) {
    return {
      attitude: row.attitude,
      midfieldInput: row.midfieldInput,
      formation: row.formation,
      tactic: row.tactic,
      simulationResult: row.simulationResult,
      hattrickTeamSpirit: row.hattrickTeamSpirit,
      currentHattrickTeamSpirit: row.currentHattrickTeamSpirit,
      currentHattrickTeamSpiritDate: row.currentHattrickTeamSpiritDate,
      hattrickSpiritBeforeInput: row.hattrickSpiritBeforeInput,
      manualSpiritBeforeInput: row.manualSpiritBeforeInput,
      hasManualAttitude: Boolean(row.hasManualAttitude),
      hasManualSpiritBefore: Boolean(row.hasManualSpiritBefore),
      midfieldAnchor: Boolean(row.midfieldAnchor),
    }
  }
  return { ...defaultCountryValues, ...(row.countryValues?.[country] || {}) }
}

export const buildPerCountryCalculation = ({
  timeline,
  matchRows,
  nationalCompetition,
  worldCompetition,
  countryGroups,
  activeNationalTeamName,
  fixtureMatchByDateCountry,
  defaultCountryValues,
  normalizeFixtureCountryKey,
  toDateKey,
  matchesCompetitionTargets,
  homeAdvantageFactor,
}) => {
  const rowsById = new Map(matchRows.map(row => [Number(row.id), row]))
  const countryState = countryGroups.reduce((accumulator, country) => {
    accumulator[country] = 5
    return accumulator
  }, {})
  const byRowKey = {}
  const matchKeysInOrder = []
  const currentHattrickAnchors = countryGroups.reduce((anchors, country) => {
    const source = matchRows
      .map(row => getCountryInputFromRow(row, country, activeNationalTeamName, defaultCountryValues))
      .find(input => parseNumber(input.currentHattrickTeamSpirit) !== null && input.currentHattrickTeamSpiritDate)
    anchors[country] = source
      ? { spirit: parseNumber(source.currentHattrickTeamSpirit), dateKey: String(source.currentHattrickTeamSpiritDate) }
      : null
    return anchors
  }, {})
  let initialAnchorApplied = false
  let previousResetDateKey = null

  timeline.forEach(row => {
    if (row.type === 'match') {
      const sourceRow = rowsById.get(Number(row.index))
      const perCountry = {}
      countryGroups.forEach(country => {
        const input = getCountryInputFromRow(
          sourceRow || {},
          country,
          activeNationalTeamName,
          defaultCountryValues
        )
        const dateKey = toDateKey(row.date)
        const fixtureCountry = fixtureMatchByDateCountry[
          `${dateKey}::${normalizeFixtureCountryKey(country)}`
        ] || null
        const venue = fixtureCountry?.venue || null
        if (previousResetDateKey && matchKeysInOrder.length === 0) {
          const updates = Math.max(0, Math.round((row.date - new Date(`${previousResetDateKey}T12:00:00`)) / (24 * 60 * 60 * 1000)))
          for (let index = 0; index < updates; index += 1) {
            countryState[country] = calculateSpiritAfterUpdate(countryState[country])
          }
        }
        const manualSpirit = parseNumber(input.manualSpiritBeforeInput)
        const spiritBefore = manualSpirit !== null
          ? manualSpirit
          : countryState[country]
        const hasManualAttitude = Boolean(input.hasManualAttitude)
        const inferredAttitude = hasManualAttitude
          ? null
          : inferAttitudeFromSpirits(
              spiritBefore,
              input.hattrickTeamSpirit
            )
        const normalizedAttitude = hasManualAttitude
          ? String(input.attitude || 'PIC').toUpperCase()
          : inferredAttitude || 'PIC'
        const coefficient = getPerformanceCoefficient(normalizedAttitude, spiritBefore)
        const spiritAfter = applyAttitudeToSpirit(spiritBefore, normalizedAttitude)
        perCountry[country] = {
          attitude: normalizedAttitude,
          midfieldInput: input.midfieldInput ?? '',
          formation: String(input.formation ?? ''),
          tactic: String(input.tactic ?? ''),
          simulationResult: input.simulationResult ?? null,
          hattrickTeamSpirit: input.hattrickTeamSpirit ?? null,
          hattrickSpiritBeforeInput: input.hattrickSpiritBeforeInput ?? null,
          manualSpiritBeforeInput: input.manualSpiritBeforeInput ?? '',
          hasManualAttitude: Boolean(input.hasManualAttitude),
          hasManualSpiritBefore: Boolean(input.hasManualSpiritBefore),
          midfieldAnchor: Boolean(input.midfieldAnchor),
          spiritBefore,
          spiritAfter,
          venue,
          coefficient,
          estimatedMatchMidfield: null,
        }
        countryState[country] = spiritAfter
      })
      byRowKey[row.key] = perCountry
      matchKeysInOrder.push(row.key)
      return
    }

    if (row.type === 'update') {
      const perCountry = {}
      countryGroups.forEach(country => {
        countryState[country] = calculateSpiritAfterUpdate(countryState[country])
        perCountry[country] = { spirit: countryState[country] }
      })
      byRowKey[row.key] = perCountry
      return
    }

    if (row.type === 'reset') {
      const nationalAllowed = matchesCompetitionTargets(nationalCompetition, row.nationalTargets || null)
      const worldAllowed = matchesCompetitionTargets(worldCompetition, row.worldTargets || null)
      if (!nationalAllowed || !worldAllowed) {
        byRowKey[row.key] = countryGroups.reduce((accumulator, country) => {
          accumulator[country] = {
            spiritBeforeReset: countryState[country],
            spiritAfterReset: countryState[country],
          }
          return accumulator
        }, {})
        return
      }
      const perCountry = {}
      countryGroups.forEach(country => {
        const anchor = currentHattrickAnchors[country]
        if (!initialAnchorApplied && anchor) {
          const resetDateKey = toDateKey(row.date)
          const updates = Math.max(0, Math.round((row.date - new Date(`${anchor.dateKey}T12:00:00`)) / (24 * 60 * 60 * 1000)))
          countryState[country] = anchor.spirit
          for (let index = 0; index < updates; index += 1) {
            countryState[country] = calculateSpiritAfterUpdate(countryState[country])
          }
          previousResetDateKey = resetDateKey
        }
        const beforeReset = countryState[country]
        const afterReset = calculateResetSpirit(beforeReset, row.resetFactor)
        perCountry[country] = { spiritBeforeReset: beforeReset, spiritAfterReset: afterReset }
        countryState[country] = afterReset
      })
      byRowKey[row.key] = perCountry
      initialAnchorApplied = true
    }
  })

  countryGroups.forEach(country => {
    let activeAnchor = null
    for (let index = 0; index < matchKeysInOrder.length - 1; index += 1) {
      const current = byRowKey[matchKeysInOrder[index]]?.[country]
      const next = byRowKey[matchKeysInOrder[index + 1]]?.[country]
      if (!current || !next) continue
      const currentMidfield = parseNumber(current.midfieldInput)
      if (current.midfieldAnchor && currentMidfield !== null && current.coefficient !== null && current.coefficient !== 0) {
        activeAnchor = { midfield: currentMidfield, coefficient: current.coefficient, venue: current.venue }
      }
      const base = activeAnchor || (currentMidfield !== null && current.coefficient !== null && current.coefficient !== 0
        ? { midfield: currentMidfield, coefficient: current.coefficient, venue: current.venue }
        : null)
      if (base && next.coefficient !== null && base.coefficient !== 0) {
        const currentVenueFactor = getVenueFactor(base.venue, homeAdvantageFactor)
        const nextVenueFactor = getVenueFactor(next.venue, homeAdvantageFactor)
        next.estimatedMatchMidfield = roundSpirit(
          base.midfield * (next.coefficient / base.coefficient) * (nextVenueFactor / currentVenueFactor)
        )
      }
    }
  })

  return byRowKey
}
